// related-products-new.js - Mirrors recently-viewed.js functionality for related products

class RelatedProductsNew {
  constructor() {
    this.maxItems = 8;

    this.container = document.getElementById("rp-new-section");
    this.grid = document.getElementById("rp-new-grid");
    this.productId = this.container?.dataset.productId;

  }

  init() {
    // If a container exists on the page, render the products
    if (this.container && this.grid && this.productId) {
      this.render();
    } else {
      console.warn(
        "[RelatedProductsNew] Container, grid, or productId missing:",
        this.container,
        this.grid,
        this.productId
      );
    }
  }

  async render() {

    // Show skeleton loaders initially (they're already in HTML)
    this.showSkeletonLoaders();

    try {
      // Use Shopify's product recommendations API
      const baseUrl = window.Shopify?.routes?.root || "/";
      const url = `${baseUrl}recommendations/products.json?product_id=${this.productId}&limit=${this.maxItems}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch recommendations: ${response.status}`);
      }

      const data = await response.json();

      if (!data.products || data.products.length === 0) {
      
        await this.renderFallback();
        return;
      }

      // Clear skeleton loaders and add real products
      this.clearSkeletonLoaders();
      const products = data.products.slice(0, this.maxItems);

      for (const product of products) {
        try {
          // Fetch pre-rendered product card HTML
          const res = await fetch(`/products/${product.handle}?view=card`);
          if (res.ok) {
            const html = await res.text();
            const wrapper = document.createElement("div");
            wrapper.innerHTML = html;
            const card = wrapper.querySelector(
              ".product-card, .card-wrapper, .card"
            );

            if (card) {
              this.grid.appendChild(card);
           
            } else {
              console.warn(
                "[RelatedProductsNew] No card found in HTML for",
                product.handle
              );
            }
          } else {
            console.warn(
              "[RelatedProductsNew] Failed to fetch card for",
              product.handle,
              res.status
            );
          }
        } catch (err) {
          console.error(
            "[RelatedProductsNew] Error fetching card for",
            product.handle,
            err
          );
        }
      }

      // Setup carousel functionality if we have products
      if (this.grid.children.length > 0) {
        this.setupCarousel(this.grid.children.length);
      } else {
        await this.renderFallback();
      }
    } catch (error) {
      console.error("Error rendering related products:", error);
      await this.renderFallback();
    }
  }

  async renderFallback() {

    // Clear skeleton loaders when starting fallback
    this.clearSkeletonLoaders();

    try {
      // Get related products using a simpler approach - fetch from the current collection
      const productJsonScript = document.querySelector('[id^="ProductJson-"]');
      if (!productJsonScript) {
        this.container.style.display = "none";
        return;
      }

      const productData = JSON.parse(productJsonScript.textContent);
      const currentProductType = productData.type;

      // Fetch products from the same type/collection
      const baseUrl = window.Shopify?.routes?.root || "/";
      const response = await fetch(
        `${baseUrl}collections/all/products.json?limit=${
          this.maxItems + 1
        }&product_type=${encodeURIComponent(currentProductType)}`
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch collection products: ${response.status}`
        );
      }

      const data = await response.json();
      const relatedProducts = data.products
        .filter((p) => p.id !== parseInt(this.productId))
        .slice(0, this.maxItems);

      if (relatedProducts.length === 0) {
        this.container.style.display = "none";
   
        return;
      }

      // Clear grid and render products
      this.grid.innerHTML = "";

      for (const product of relatedProducts) {
        try {
          // Fetch pre-rendered product card HTML
          const res = await fetch(`/products/${product.handle}?view=card`);
          if (res.ok) {
            const html = await res.text();
            const wrapper = document.createElement("div");
            wrapper.innerHTML = html;
            const card = wrapper.querySelector(
              ".product-card, .card-wrapper, .card"
            );

            if (card) {
              this.grid.appendChild(card);
           
            }
          }
        } catch (err) {
          console.error(
            "[RelatedProductsNew] Error fetching card for",
            product.handle,
            err
          );
        }
      }

      // Setup carousel functionality
      this.setupCarousel(relatedProducts.length);
    } catch (error) {
      console.error("Error in renderFallback:", error);
      this.container.style.display = "none";
    }
  }

  setupCarousel(itemCount) {
    const prevButton = this.container.querySelector(
      ".rp-new-carousel-button--prev"
    );
    const nextButton = this.container.querySelector(
      ".rp-new-carousel-button--next"
    );

    if (!prevButton || !nextButton) {
      console.warn("[RelatedProductsNew] Carousel buttons not found");
      return;
    }

   

    // Always show the carousel buttons since it's always carousel
    prevButton.parentElement.style.display = "flex";

    // Always initialize carousel controls since it's always carousel
    if (itemCount > 0) {
      this.initCarouselControls(prevButton, nextButton);
    } else {
      prevButton.parentElement.style.display = "none";
    }
  }

  initCarouselControls(prevButton, nextButton) {
    // Remove any existing event listeners
    const newPrevButton = prevButton.cloneNode(true);
    const newNextButton = nextButton.cloneNode(true);
    prevButton.parentNode.replaceChild(newPrevButton, prevButton);
    nextButton.parentNode.replaceChild(newNextButton, nextButton);

    const updateButtons = () => {
      const maxScroll = this.grid.scrollWidth - this.grid.clientWidth;
      newPrevButton.disabled = this.grid.scrollLeft <= 0;
      newNextButton.disabled = this.grid.scrollLeft >= maxScroll;
    };

    const scrollAmount = () => {
      if (window.innerWidth < 750) return this.grid.clientWidth / 2; // Scroll half width on mobile
      if (window.innerWidth < 990) return this.grid.clientWidth / 3; // Scroll third width on tablet
      return this.grid.clientWidth / 4; // Scroll quarter width on desktop
    };

    newPrevButton.addEventListener("click", () => {
      this.grid.scrollBy({ left: -scrollAmount(), behavior: "smooth" });
      setTimeout(updateButtons, 300);
    });

    newNextButton.addEventListener("click", () => {
      this.grid.scrollBy({ left: scrollAmount(), behavior: "smooth" });
      setTimeout(updateButtons, 300);
    });

    // Initialize button states
    setTimeout(updateButtons, 100);

    // Update buttons on scroll
    this.grid.addEventListener("scroll", updateButtons);
  }

  showSkeletonLoaders() {
    // Make sure the skeleton wrapper is visible
    const wrapper = this.grid.querySelector(".rp-new__skeleton-wrapper");
    if (wrapper) {
      wrapper.style.display = "flex";
    }
  }

  clearSkeletonLoaders() {
    // Remove the skeleton wrapper
    const wrapper = this.grid.querySelector(".rp-new__skeleton-wrapper");
    if (wrapper) {
      wrapper.remove();
    }
  }
}

// --- INITIALIZATION ---
document.addEventListener("DOMContentLoaded", () => {

  // Initialize related products
  const relatedProductsNew = new RelatedProductsNew();
  relatedProductsNew.init();
});
